import {useMemo, useState} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {createReportColumn} from '../helpers/report-builder.helpers';
import {REPORT_BEFORE_RUN_HINT} from '../constants/report-editor.constants';
import {ReportFieldsEditorProps} from '../types/report-editor.types';
import {ReportColumnFilter} from './report-column-filter';
import styles from './report-editor.module.scss';

export const ReportFieldsEditor = ({fields, columns, errors, disabled, onChange}: ReportFieldsEditorProps) => {
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<string[]>([]);
  const selected = new Set(columns.map((column) => column.key));
  const available = fields.filter((field) => !selected.has(field.key));
  const visible = available.filter((field) =>
    field.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const fieldsByKey = useMemo(() => new Map(fields.map((field) => [field.key, field])), [fields]);
  const checkedAvailable = checked.filter((key) => !selected.has(key));

  const addSelected = () => {
    onChange([...columns, ...checkedAvailable.map(createReportColumn)]);
    setChecked([]);
  };
  const moveColumn = (index: number, direction: number) => {
    const next = [...columns];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    onChange(next);
  };
  return (
    <div className={styles.fieldsLayout}>
      <section className={styles.availablePanel} aria-label="Доступные поля">
        <h3>
          Доступные поля <span>{available.length}</span>
        </h3>
        <TextField
          fullWidth
          size="small"
          placeholder="Поиск поля"
          value={search}
          disabled={disabled}
          inputProps={{'aria-label': 'Поиск поля'}}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.availableList}>
          {visible.map((field) => (
            <FormControlLabel
              key={field.key}
              label={field.label}
              control={
                <Checkbox
                  size="small"
                  disabled={disabled}
                  checked={checked.includes(field.key)}
                  onChange={(_, nextChecked) =>
                    setChecked((current) =>
                      nextChecked ? [...current, field.key] : current.filter((key) => key !== field.key),
                    )
                  }
                />
              }
            />
          ))}
          {!visible.length && (
            <p className={styles.empty}>{available.length ? 'Поля не найдены' : 'Все поля выбраны'}</p>
          )}
        </div>
      </section>
      <div className={styles.transferActions}>
        <Tooltip title="Добавить выбранные поля">
          <span>
            <IconButton
              aria-label="Добавить выбранные поля"
              disabled={disabled || !checkedAvailable.length}
              onClick={addSelected}
            >
              <ArrowForwardIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Убрать все поля">
          <span>
            <IconButton
              aria-label="Убрать все поля"
              disabled={disabled || !columns.length}
              onClick={() => onChange([])}
            >
              <ArrowBackIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <section className={styles.selectedPanel} aria-label="Выбранные поля">
        <h3>
          Выбранные поля <span>{columns.length}</span>
        </h3>
        <div className={styles.columnHeader}>
          <span>Порядок / поле</span>
          <span>Фильтр</span>
          <span className={styles.beforeRunHeading}>
            Изменять перед запуском
            <Tooltip title={REPORT_BEFORE_RUN_HINT}>
              <InfoOutlinedIcon tabIndex={0} aria-label="Об изменении перед запуском" sx={{fontSize: 16}} />
            </Tooltip>
          </span>
        </div>
        <div className={styles.selectedList}>
          {columns.map((column, index) => {
            const field = fieldsByKey.get(column.key);
            if (!field) return null;
            return (
              <div key={column.key} className={styles.selectedRow}>
                <div className={styles.columnName}>
                  <div className={styles.order}>
                    <span className={styles.position}>{index + 1}</span>
                    <div>
                      <IconButton
                        size="small"
                        aria-label={`Поднять ${field.label}`}
                        disabled={disabled || index === 0}
                        onClick={() => moveColumn(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Опустить ${field.label}`}
                        disabled={disabled || index === columns.length - 1}
                        onClick={() => moveColumn(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </div>
                  </div>
                  <span>{field.label}</span>
                </div>
                <ReportColumnFilter
                  field={field}
                  column={column}
                  error={errors[column.key]}
                  disabled={disabled}
                  onChange={(next) => onChange(columns.map((item) => (item.key === column.key ? next : item)))}
                />
                <FormControlLabel
                  className={styles.beforeRunOption}
                  label={<span className={styles.beforeRunLabel}>Изменять перед запуском</span>}
                  control={
                    <Checkbox
                      size="small"
                      checked={column.isConstant === true}
                      disabled={disabled}
                      inputProps={{'aria-label': `Изменять перед запуском: ${field.label}`}}
                      onChange={(_, checked) =>
                        onChange(
                          columns.map((item) => (item.key === column.key ? {...item, isConstant: checked} : item)),
                        )
                      }
                    />
                  }
                />
                <IconButton
                  className={styles.removeColumn}
                  size="small"
                  aria-label={`Удалить ${field.label}`}
                  disabled={disabled}
                  onClick={() => onChange(columns.filter((item) => item.key !== column.key))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            );
          })}
          {!columns.length && (
            <p className={styles.empty}>Выберите поля слева и добавьте их в отчёт с помощью стрелки.</p>
          )}
        </div>
        <Alert
          role="note"
          severity="info"
          variant="outlined"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{
            mt: 1.5,
            px: 1,
            py: 0.5,
            backgroundColor: '#eaf1ff',
            borderColor: '#accbff',
            color: '#111827',
            fontSize: 14,
            lineHeight: 1.5,
            '& .MuiAlert-icon': {color: '#007dff', fontSize: 18, mr: 1},
          }}
        >
          {REPORT_BEFORE_RUN_HINT}
        </Alert>
      </section>
    </div>
  );
};
